/**
 * What counts as the same sound.
 *
 * Two different things are folded together here and it is worth keeping them apart in your
 * head, because they justify themselves differently:
 *
 *   1. **Dialect variation.** A Sichuanese speaker does not distinguish zh/z, ch/c, sh/s. A
 *      southern speaker merges -n and -ng. Neither is an error — the parent at the gate said
 *      the name correctly, in their own Mandarin, and the roster spells it in another.
 *      These cost near zero.
 *   2. **Recogniser error.** An English-language recogniser has no phoneme for pinyin q, x
 *      or zh and reaches for whichever English spelling is nearest, picking a different one
 *      each time. These cost a little more than dialect variation but far less than a real
 *      mismatch.
 *
 * Tone is ignored entirely and deliberately. A recogniser working in English does not
 * transcribe tone at all, so a tone-aware cost has nothing to read.
 */

/** Cost of substituting one initial for another. 0 = identical, 1 = unrelated. */
const INITIAL_PAIRS: [string, string, number][] = [
  // Retroflex/alveolar merger — the biggest single dialect split in the room.
  ['zh', 'z', 0.05], ['ch', 'c', 0.05], ['sh', 's', 0.05],
  // n/l merger (southern), r/l (both directions of the same difficulty).
  ['n', 'l', 0.1], ['r', 'l', 0.15], ['r', 'y', 0.2],
  // h/f — Hokkien and parts of Hunan.
  ['h', 'f', 0.15],
  // The palatals have no English equivalent, so a recogniser spells them by ear.
  ['j', 'zh', 0.2], ['q', 'ch', 0.2], ['x', 'sh', 0.2], ['x', 's', 0.3], ['j', 'z', 0.3],
  ['q', 'c', 0.3], ['q', 'k', 0.35], ['j', 'g', 0.35],
  // Wade-Giles and older romanisations, which is how an older sibling's name got spelled.
  ['b', 'p', 0.25], ['d', 't', 0.25], ['g', 'k', 0.25],
  // Voiced/voiceless confusions an English ear makes over background noise.
  ['zh', 'j', 0.2], ['z', 's', 0.3], ['c', 's', 0.3],
  ['w', 'v', 0.15], ['w', 'h', 0.4],
  // Both are made at the back of the mouth, and Mandarin's k- carries an aspiration strong
  // enough that an English ear writes it as an h: kǎi comes back inside "Shanghai".
  ['k', 'h', 0.4],
];

/** Cost of substituting one final for another. */
const FINAL_PAIRS: [string, string, number][] = [
  // Nasal merger. Southern Mandarin does not distinguish these at all.
  ['an', 'ang', 0.05], ['en', 'eng', 0.05], ['in', 'ing', 0.05],
  ['ian', 'iang', 0.08], ['uan', 'uang', 0.08], ['on', 'ong', 0.05],
  // Contracted spellings — the same sound written short or long.
  ['ui', 'uei', 0.0], ['iu', 'iou', 0.0], ['un', 'uen', 0.0],
  // ü written every way a keyboard allows.
  ['v', 'u', 0.05], ['ve', 'ue', 0.0], ['van', 'uan', 0.1], ['vn', 'un', 0.05],
  // Vowels an English recogniser reaches past.
  ['e', 'er', 0.2], ['e', 'uo', 0.3], ['o', 'uo', 0.15], ['o', 'ou', 0.2],
  ['i', 'ie', 0.3], ['i', 'ei', 0.3], ['ai', 'ei', 0.3], ['ao', 'ou', 0.3],
  ['ia', 'a', 0.3], ['ua', 'a', 0.3], ['iao', 'ao', 0.2], ['iu', 'ou', 0.25],
  // Medial-glide loss, completing the family the four pairs above had started. English has
  // no -ü- and barely any -i-/-u- on-glide, so a recogniser hears xuān and writes "shan" or
  // "shang": the glide is the first thing it drops. The pairs that also cross the -n/-ng
  // merger cost a little more, being two changes rather than one.
  ['uan', 'an', 0.3], ['uan', 'ang', 0.34], ['ian', 'an', 0.3], ['ian', 'ang', 0.34],
  ['uang', 'ang', 0.3], ['iang', 'ang', 0.3], ['van', 'an', 0.3], ['van', 'ang', 0.34],
  ['uai', 'ai', 0.3], ['uei', 'ei', 0.3], ['ui', 'ei', 0.3], ['ue', 'e', 0.3],
  ['ie', 'e', 0.3], ['iong', 'ong', 0.3],
  ['u', 'ou', 0.3], ['u', 'o', 0.3], ['a', 'e', 0.4],
];

function buildCosts(pairs: [string, string, number][]): Map<string, number> {
  const m = new Map<string, number>();
  for (const [a, b, cost] of pairs) {
    m.set(`${a}|${b}`, cost);
    m.set(`${b}|${a}`, cost);
  }
  return m;
}

const INITIAL_COST = buildCosts(INITIAL_PAIRS);
const FINAL_COST = buildCosts(FINAL_PAIRS);

/**
 * Cost of hearing `a` where the roster says `b`, for one initial. 0–1.
 *
 * A dropped initial is cheap in one specific direction: a recogniser drops a weak initial
 * far more readily than it invents one, and "an" for "han" is a hearing this actually makes.
 */
export function initialCost(a: string, b: string): number {
  if (a === b) return 0;
  if (a === '' || b === '') return 0.45;
  return INITIAL_COST.get(`${a}|${b}`) ?? 1;
}

/** Cost of hearing `a` where the roster says `b`, for one final. 0–1. */
export function finalCost(a: string, b: string): number {
  if (a === b) return 0;
  if (a === '' || b === '') return 1;
  return FINAL_COST.get(`${a}|${b}`) ?? 1;
}

/**
 * How alike two syllables are, 0–1.
 *
 * The initial carries slightly less weight than the final. That is not arbitrary: the final
 * is longer, louder and more of the syllable's identity, and the initials are exactly where
 * both dialect variation and recogniser guesswork concentrate. Weighting them equally made
 * "Zhang" and "Chang" score the same as "Zhang" and "Zhong", which is wrong — the first pair
 * is one person and the second is two.
 */
export function syllableSimilarity(a: { initial: string; final: string }, b: { initial: string; final: string }): number {
  const cost = 0.4 * initialCost(a.initial, b.initial) + 0.6 * finalCost(a.final, b.final);
  return Math.max(0, 1 - cost);
}
